'use client';

import Link from '@/components/WrappedLink';
import Image from 'next/image';
import colors from '@/theme/colors.module.scss';
import LinkButton, {
    EButtonVariant,
} from '../../Button/link-button/link-button.component';
import {
    AboutUsIcon,
    BuyCarIcon,
    FAQsIcon,
    FinanceLogo,
    SellCarIcon,
    SylndrLogo,
    BlogIcon,
    AppProfileEntryPoint,
    InspectionEntryPointArabic,
    InspectionEntryPointEnglish,
} from '../../../../../public/NavBar';
import { ChevronRight, ProfileIconGrey } from '../../../../../public/Images';

import { usePathname } from 'next/navigation';
import styles from './styles.module.scss';

import Typography from '@/components/shared/typography/typography.component';
import ChangeLanguage from '../change-langauge-client.component';
import { useTranslation } from '@/hook';
import useAuth from '@/hook/use-auth.hook';
import {
    AVAILABLE_LANGUAGES,
    FEATURE_FLAG_SHOW_INSPECTION_PLUS_BANNER,
    NEXT_PUBLIC_VERCEL_URL,
} from '@/constants';
import { useAnalytics } from '@/integrations';
import { AuthStepsEnum } from '@/types';
import { useEffect, useState } from 'react';
import Button from '../../Button/button.component';
import myImageLoader from '@/image.local.loader';
import { openApp } from '@/utils';
import useStoreLink from '@/hook/use-appstore.hook';

interface Props {
    lang: AVAILABLE_LANGUAGES;
}
interface ILink {
    id: number;
    label: string;
    path: string;
    pathName: string;
    image: any;
    newItem?: boolean;
}

export default function MobileNav({ lang }: Props) {
    const { t } = useTranslation();
    const pathname = usePathname();
    const { trackEvent } = useAnalytics();
    const {
        updateCurrentAuthStep,
        userId,
        userInitials,
        name,
        phone,
        email,
        signInOrUpClicked,
        isLoggedIn,
    } = useAuth();
    const LINKS: ILink[] = [
        {
            id: 0,
            label: 'Browse All Cars',
            path: `/${lang}/buy-used-cars`,
            pathName: 'buy-used-cars',
            image: BuyCarIcon,
        },
        {
            id: 2,
            label: 'Sell Your Car',
            path: `/${lang}/sell-car`,
            pathName: 'sell-car',
            image: SellCarIcon,
        },
        {
            id: 3,
            label: 'Why Sylndr',
            path: `/${lang}/why-sylndr`,
            pathName: 'why-sylndr',
            image: SylndrLogo,
        },
        {
            id: 4,
            label: 'FAQs',
            path: `/${lang}/how-it-works`,
            pathName: 'how-it-works',
            image: FAQsIcon,
        },
        {
            id: 5,
            label: 'Finance',
            path: `/${lang}/car-finance`,
            pathName: 'car-finance',
            image: FinanceLogo,
        },
        {
            id: 6,
            label: 'About us',
            path: `/${lang}/about-us`,
            pathName: 'about-us',
            image: AboutUsIcon,
        },
        {
            id: 7,
            label: 'Blog',
            path: 'https://sylndr.com/ar/blog/home/',
            pathName: 'blog',
            image: BlogIcon,
        },
    ];
    const [linksState, setLinksState] = useState(LINKS);
    const storeLink = useStoreLink();
    useEffect(() => {
        if (userId?.length) {
            const myAccountAlreadyExists = linksState.some(
                obj => obj.label === 'myAccount'
            );
            if (!myAccountAlreadyExists) {
                const myAccountItem: ILink = {
                    id: Date.now(),
                    label: 'myAccount',
                    path: `/${lang}/account`,
                    pathName: 'account',
                    image: ProfileIconGrey,
                    newItem: true,
                };
                const newArray = [
                    ...LINKS.slice(0, 2),
                    myAccountItem,
                    ...LINKS.slice(2),
                ];
                setLinksState(newArray);
            }
        } else {
            setLinksState(LINKS);
        }
    }, [userId]);

    const returnCurrentPage = () => {
        const arr = pathname.split('/');
        const page = arr[arr.length - 1];
        return page;
    };
    const fireOnClickeBehavior = (label: string) => {
        if (label === 'Finance') {
            return trackEvent('Screen: Financing Page', {
                sourceScreen: 'Header',
            });
        }
        if (label === 'myAccount') {
            return updateCurrentAuthStep(AuthStepsEnum.profile);
        }
    };

    return (
        <>
            <div className={styles.tagContainer}>
                {isLoggedIn ? (
                    <div className={styles.contentWrapper}>
                        <div className={styles.iconContainer}>
                            <Typography variant="subtitle1Medium">
                                {userInitials}
                            </Typography>
                        </div>
                        <div className={styles.userDataContainer}>
                            <Typography variant="body2SemiBold" component="div">
                                {name}
                            </Typography>
                            <Typography variant="caption" component="div">
                                {phone}
                            </Typography>
                        </div>
                    </div>
                ) : (
                    <div
                        className={styles.contentWrapper}
                        onClick={() => signInOrUpClicked()}
                    >
                        <div className={styles.iconContainer}>
                            <Image
                                width={32}
                                height={32}
                                src={ProfileIconGrey}
                                alt={'ProfileIconGrey'}
                            />
                        </div>
                        <div>
                            <Typography
                                variant="body2SemiBold"
                                className={styles.signInLabel}
                            >
                                {t('signIn')}
                            </Typography>
                            <Typography variant="caption">
                                {t('dontHaveAnAccount')}
                            </Typography>
                            <Typography variant="caption" component="div">
                                <span className={styles.signUpLabel}>
                                    {t('signUp')}
                                </span>
                            </Typography>
                        </div>
                    </div>
                )}
            </div>
            <ul className={styles.linksContainer}>
                {linksState.map(link => {
                    const isElajans = link.pathName === 'elajans';

                    return (
                        <li key={link.id} className={styles.navItem}>
                            <div className={styles.navItemWrapper}>
                                <Image
                                    width={160}
                                    height={26}
                                    src={link.image}
                                    alt={link.label}
                                    className={styles.icon}
                                />
                                {isElajans ? (
                                    <Link
                                        href={`${NEXT_PUBLIC_VERCEL_URL}/elajans/${lang}`}
                                        target="_blank"
                                        locale={false} // ← this must hit next/link directly
                                        style={{
                                            color:
                                                returnCurrentPage() ===
                                                'elajans'
                                                    ? colors.primary600
                                                    : colors.gray900,
                                        }}
                                        onClick={() =>
                                            trackEvent(
                                                'Click On El-Ajans Internal',
                                                {
                                                    lang,
                                                    source: 'Burger Menu',
                                                }
                                            )
                                        }
                                    >
                                        <Typography variant="body1Medium">
                                            {t(link.label as any)}
                                        </Typography>
                                    </Link>
                                ) : (
                                    <Link
                                        href={link.path}
                                        style={{
                                            color:
                                                link.pathName ===
                                                returnCurrentPage()
                                                    ? colors.primary600
                                                    : colors.gray900,
                                        }}
                                        onClick={() =>
                                            fireOnClickeBehavior(link.label)
                                        }
                                    >
                                        <Typography variant="body1Medium">
                                            {t(link.label as any)}
                                        </Typography>
                                    </Link>
                                )}
                            </div>

                            {link.newItem && (
                                <div className={styles.newItemWrapper}>
                                    <Typography variant="captionMedium">
                                        {t('newLabel')}
                                    </Typography>
                                </div>
                            )}
                        </li>
                    );
                })}
                <li
                    className={`${styles.navItem} ${styles.langItem} ${styles.langSelectorMobile}`}
                >
                    <ChangeLanguage lang={lang} />
                </li>
            </ul>
            <hr style={{ border: 'none', background: 'lighgray' }} />
            {FEATURE_FLAG_SHOW_INSPECTION_PLUS_BANNER ? (
                <div className="flex justify-center px-4 pt-2">
                    <Link
                        href={`https://get.sylndr.com/${lang}/sylndr-plus`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <img
                            className="w-full"
                            alt="entry-application-profile "
                            src={myImageLoader({
                                src:
                                    lang === 'en'
                                        ? InspectionEntryPointEnglish.src
                                        : InspectionEntryPointArabic.src,
                                quality: 100,
                            })}
                        />
                    </Link>
                </div>
            ) : (
                <div className="px-2 pt-2 border-t-2 border-t-gray-200 ">
                    <div className="relative">
                        <img
                            className="ar:scale-x-[-1]"
                            alt="entry-application-profile "
                            src={myImageLoader({
                                src: AppProfileEntryPoint.src,
                                width: 800,
                                height: 800,
                                quality: 100,
                            })}
                        />
                        <div className="absolute en:right-6 ar:left-[18px] en:w-[150px] ar:w-[160px] h-[90px] top-5 flex flex-col justify-around">
                            <Typography
                                className="!text-gray-800"
                                variant="body2SemiBold"
                            >
                                {t('getSylndrAppProfile')}
                            </Typography>
                            <Button
                                className="ar:!w-[155px] en:!w-[150px] !h-[34px] flex items-center gap-2"
                                onClick={() => {
                                    trackEvent('Click On Download App', {
                                        userId,
                                        sourceButton: 'Burger Menu',
                                        phone,
                                        email,
                                    });
                                    openApp({
                                        targetScreen: 'ProfileScreen',
                                        storeLink,
                                    });
                                }}
                            >
                                <Typography
                                    variant="captionMedium"
                                    className="ar:mt-0.5"
                                >
                                    {t('getApp')}
                                </Typography>
                                <Image
                                    className="ar:rotate-180"
                                    height={11}
                                    src={ChevronRight}
                                    alt="arrow-right"
                                />
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <div className={styles.buttonsContainer}>
                <LinkButton
                    style={{ height: '48px' }}
                    variant={EButtonVariant.PRIMARY}
                    href={`/${lang}/buy-used-cars`}

                    //   state={{ sourcePage: 'Buy_Header' }}
                >
                    {t('Buy Car')}
                </LinkButton>

                <LinkButton
                    style={{ height: '48px' }}
                    href={`/${lang}/sell-car`}
                    variant={EButtonVariant.INVERTED}
                >
                    {t('Sell Car')}
                </LinkButton>
            </div>
        </>
    );
}
